import { useCallback, useEffect, useState } from 'react';

import LoadingButton from 'src/components/atoms/buttons/LoadingButton';
import { AgInput, AgModal, AgModalActions, AgModalContent, AgRadio, AgRadioGroup } from 'src/components/ui';
import { AgAutocomplete } from 'src/components/ui/AgAutocomplete';

export enum ExpenseAction {
  CREATING,
  UPDATING,
}

enum ChargeTypeAccountLevel {
  RECEIVABLE = 'receivable',
  PAYABLE = 'payable',
  BOTH = 'both',
}

export default function ExpenseTypeActionModal({
  onClose,
  incomeAccounts,
  payableAccounts,
  expenseAction,
  handleExpenseAction,
  expenseRows,
}) {
  const [chargeName, setChargeName] = useState('');
  const [accountLevel, setAccountLevel] = useState(ChargeTypeAccountLevel.BOTH);
  const [receivablesAccount, setReceivablesAccount] = useState(null);
  const [payablesAccount, setPayablesAccount] = useState(null);
  const [nameError, setNameError] = useState<string>(null);

  useEffect(() => {
    const value = expenseAction?.value;

    if (value) {
      const isPayable = value.accountLevels?.includes('ACCOUNTS_PAYABLE');
      const isReceivable = value.accountLevels?.includes('ACCOUNTS_RECEIVABLE');
      const isBoth = isPayable && isReceivable;

      let accountLevel = ChargeTypeAccountLevel.BOTH;

      if (!isBoth) {
        accountLevel = isPayable ? ChargeTypeAccountLevel.PAYABLE : ChargeTypeAccountLevel.RECEIVABLE;
      }

      setChargeName(value.name);
      setAccountLevel(accountLevel);
      setReceivablesAccount(value.receivablesAccount);
      setPayablesAccount(value.payablesAccount);
    } else {
      setChargeName('');
      setAccountLevel(ChargeTypeAccountLevel.BOTH);
      setReceivablesAccount(null);
      setPayablesAccount(null);
    }

    setNameError(null);
  }, [expenseAction]);

  const selectedExpenseType = expenseAction?.value;
  const isActivePayable = selectedExpenseType?.isActivePayable;
  const isActiveReceivable = selectedExpenseType?.isActiveReceivable;

  const header = expenseAction?.type === ExpenseAction.CREATING ? 'Add Charge' : 'Edit Charge';

  const handleAction = useCallback(() => {
    const accountLevels = [];
    const isBoth = accountLevel === ChargeTypeAccountLevel.BOTH;
    const isPayable = accountLevel === ChargeTypeAccountLevel.PAYABLE;
    const isReceivable = accountLevel === ChargeTypeAccountLevel.RECEIVABLE;

    if (isPayable || isBoth) {
      accountLevels.push('ACCOUNTS_PAYABLE', 'GROWER_PAYABLE');
    }

    if (isReceivable || isBoth) {
      accountLevels.push('ACCOUNTS_RECEIVABLE');
    }

    const input = {
      name: chargeName.trim(),
      accountLevels,
      receivablesAccountId: isPayable ? null : receivablesAccount?.id,
      payablesAccountId: isReceivable ? null : payablesAccount?.id,
    };

    return handleExpenseAction(input);
  }, [accountLevel, chargeName, handleExpenseAction, payablesAccount, receivablesAccount]);

  return (
    <AgModal open={!!expenseAction} onClose={onClose} title={header}>
      <AgModalContent>
        <div className="flex flex-col gap-6 py-2">
          <AgInput
            label="Name"
            helperText={nameError ? undefined : 'The name of the charge will be displayed when adding charges.'}
            errorMessage={nameError}
            value={chargeName}
            onChange={setChargeName}
            isRequired
          />
          <AgRadioGroup
            label="Charge Type"
            value={accountLevel}
            onChange={(value) => setAccountLevel(value as ChargeTypeAccountLevel)}
          >
            <AgRadio value={ChargeTypeAccountLevel.RECEIVABLE} label="Receivable" isDisabled={isActivePayable} />
            <AgRadio value={ChargeTypeAccountLevel.PAYABLE} label="Payable" isDisabled={isActiveReceivable} />
            <AgRadio value={ChargeTypeAccountLevel.BOTH} label="Both" />
          </AgRadioGroup>

          <AgAutocomplete
            value={accountLevel === ChargeTypeAccountLevel.PAYABLE ? null : receivablesAccount}
            label="Receivables Account"
            isDisabled={accountLevel === ChargeTypeAccountLevel.PAYABLE}
            options={incomeAccounts}
            getOptionLabel={(opt) => opt.accountName}
            getOptionKey={(opt) => opt.id}
            onChange={setReceivablesAccount}
          />
          <AgAutocomplete
            value={accountLevel === ChargeTypeAccountLevel.RECEIVABLE ? null : payablesAccount}
            label="Payables Account"
            isDisabled={accountLevel === ChargeTypeAccountLevel.RECEIVABLE}
            options={payableAccounts}
            getOptionLabel={(opt) => opt.accountName}
            getOptionKey={(opt) => opt.id}
            onChange={setPayablesAccount}
          />
        </div>
      </AgModalContent>
      <AgModalActions>
        <LoadingButton
          variant="primary"
          validateRequest={() => {
            if (!chargeName) {
              setNameError('Charge name is required.');
              return false;
            }

            const alreadyExists = expenseRows.some(
              (r) => r.name === chargeName.trim() && r.id !== selectedExpenseType?.id
            );

            if (alreadyExists) {
              setNameError('A charge with this name already exists.');
              return false;
            }
          }}
          handleRequest={handleAction}
          handleRequestComplete={() => {
            onClose();
          }}
        >
          Save
        </LoadingButton>
      </AgModalActions>
    </AgModal>
  );
}
